import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { DashboardLayout } from '@/components/DashboardLayout';
import {
  MessageSquare, Save, RefreshCw, Info, CheckCircle,
  ToggleLeft, ToggleRight, Send, AlertTriangle, Phone,
  Calendar, ChevronDown, ChevronUp, Badge
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── helpers ────────────────────────────────────────────────────────────────────
const HOURS   = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES = ['00', '15', '30', '45'];

const TRACK_META = {
  A: {
    label: 'Track A — Signup Incomplete',
    color: 'amber',
    icon: '📋',
    desc: 'Lead captured → follows up until signup is done',
    days: [
      { day: 'D0', env: 'AISENSY_CAMPAIGN_TRACK_A_D0', campaign: 'cs_signup_d0' },
      { day: 'D2', env: 'AISENSY_CAMPAIGN_TRACK_A_D2', campaign: 'cs_signup_d2' },
      { day: 'D4', env: 'AISENSY_CAMPAIGN_TRACK_A_D4', campaign: 'cs_signup_d4' },
    ],
  },
  B: {
    label: 'Track B — KYC Pending',
    color: 'blue',
    icon: '🪪',
    desc: 'Signed up → follows up until KYC documents are submitted',
    days: [
      { day: 'D0', env: 'AISENSY_CAMPAIGN_TRACK_B_D0', campaign: 'cs_signup_thanks' },
      { day: 'D1', env: 'AISENSY_CAMPAIGN_TRACK_B_D1', campaign: 'cs_kyc_d1' },
      { day: 'D2', env: 'AISENSY_CAMPAIGN_TRACK_B_D2', campaign: 'cs_kyc_d2' },
      { day: 'D4', env: 'AISENSY_CAMPAIGN_TRACK_B_D4', campaign: 'cs_kyc_d4' },
    ],
  },
  C: {
    label: 'Track C — First Order Pending',
    color: 'purple',
    icon: '📦',
    desc: 'KYC submitted → follows up until first order is placed',
    days: [
      { day: 'D2', env: 'AISENSY_CAMPAIGN_TRACK_C_D2', campaign: 'cs_first_order_d2' },
      { day: 'D4', env: 'AISENSY_CAMPAIGN_TRACK_C_D4', campaign: 'cs_signup_offer_d4' },
    ],
  },
};

const STATUS_BADGE = {
  sent:      'bg-green-100 text-green-700',
  failed:    'bg-red-100 text-red-700',
  opted_out: 'bg-slate-100 text-slate-600',
  delivered: 'bg-blue-100 text-blue-700',
  read:      'bg-violet-100 text-violet-700',
};

function TrackToggle({ track, enabled, onChange }) {
  const m = TRACK_META[track];
  const colorMap = {
    amber:  { bg: 'bg-amber-50', border: 'border-amber-200', icon: 'bg-amber-100 text-amber-700', badge: 'bg-amber-100 text-amber-700', day: 'bg-amber-50 text-amber-600 border-amber-200' },
    blue:   { bg: 'bg-blue-50',  border: 'border-blue-200',  icon: 'bg-blue-100 text-blue-700',   badge: 'bg-blue-100 text-blue-700',   day: 'bg-blue-50 text-blue-600 border-blue-200'   },
    purple: { bg: 'bg-violet-50',border: 'border-violet-200',icon: 'bg-violet-100 text-violet-700',badge: 'bg-violet-100 text-violet-700',day: 'bg-violet-50 text-violet-600 border-violet-200'},
  };
  const c = colorMap[m.color];

  return (
    <div className={`rounded-xl border ${c.border} ${c.bg} px-5 py-4`}>
      <div className="flex items-start gap-4">
        <div className={`w-8 h-8 rounded-lg ${c.icon} flex items-center justify-center text-base flex-shrink-0 mt-0.5`}>
          {m.icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-slate-800 text-sm">{m.label}</span>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${c.badge}`}>
              {enabled ? 'Active' : 'Paused'}
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-0.5 mb-2">{m.desc}</p>
          {/* Per-day campaign pills */}
          <div className="flex flex-wrap gap-1.5">
            {m.days.map(({ day, campaign }) => (
              <span key={day}
                className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded border font-mono ${c.day}`}>
                <span className="font-bold">{day}</span>
                <span className="opacity-70">→</span>
                <span>{campaign}</span>
              </span>
            ))}
          </div>
        </div>
        <button
          type="button"
          onClick={() => onChange(!enabled)}
          data-testid={`track-${track.toLowerCase()}-toggle`}
          className="flex-shrink-0 mt-0.5"
          title={enabled ? 'Disable track' : 'Enable track'}
        >
          {enabled
            ? <ToggleRight className="w-8 h-8 text-green-500" />
            : <ToggleLeft  className="w-8 h-8 text-slate-300" />
          }
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

export default function WhatsAppSettings() {
  // ── Legacy KYC reminder settings ─────────────────────────────────────────
  const [legacy, setLegacy]         = useState({ kyc_reminder_delay_hours: 24, kyc_reminder_time_hour: 10, kyc_reminder_time_minute: 0, kyc_reminder_ampm: 'AM' });
  const [legacyLoading, setLL]      = useState(true);
  const [legacySaving, setLS]       = useState(false);
  const [legacySaved, setLSaved]    = useState(false);

  // ── Automation settings ───────────────────────────────────────────────────
  const [auto, setAuto]             = useState({ track_a_enabled: true, track_b_enabled: true, track_c_enabled: true, offer_text: '', alert_email: '', frontend_base_url: 'https://connectdesk.cloudsetu.com' });
  const [autoLoading, setAL]        = useState(true);
  const [autoSaving, setAS]         = useState(false);
  const [autoSaved, setASaved]      = useState(false);

  // ── Logs ─────────────────────────────────────────────────────────────────
  const [logs, setLogs]             = useState([]);
  const [logsTotal, setLogsTotal]   = useState(0);
  const [logsLoading, setLogsLoad]  = useState(false);
  const [logTrack, setLogTrack]     = useState('');
  const [logPage, setLogPage]       = useState(0);
  const [showLogs, setShowLogs]     = useState(false);

  const LOG_LIMIT = 50;

  // ── Load data ──────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchLegacy();
    fetchAuto();
  }, []);

  useEffect(() => {
    if (showLogs) fetchLogs(0, logTrack);
  }, [showLogs, logTrack]); // eslint-disable-line

  const fetchLegacy = async () => {
    setLL(true);
    try {
      const r = await axios.get(`${API}/admin/whatsapp-settings`);
      setLegacy(r.data);
    } catch { toast.error('Could not load legacy settings'); }
    finally { setLL(false); }
  };

  const fetchAuto = async () => {
    setAL(true);
    try {
      const r = await axios.get(`${API}/admin/whatsapp-automation/settings`);
      setAuto(r.data);
    } catch { toast.error('Could not load automation settings'); }
    finally { setAL(false); }
  };

  const fetchLogs = useCallback(async (page = 0, track = '') => {
    setLogsLoad(true);
    try {
      const params = { limit: LOG_LIMIT, skip: page * LOG_LIMIT };
      if (track) params.track = track;
      const r = await axios.get(`${API}/admin/whatsapp-automation/logs`, { params });
      setLogs(r.data.logs || []);
      setLogsTotal(r.data.total || 0);
      setLogPage(page);
    } catch { toast.error('Could not load message logs'); }
    finally { setLogsLoad(false); }
  }, []);

  // ── Save handlers ──────────────────────────────────────────────────────────
  const saveLegacy = async () => {
    setLS(true);
    try {
      await axios.put(`${API}/admin/whatsapp-settings`, legacy);
      toast.success('KYC reminder settings saved');
      setLSaved(true);
      setTimeout(() => setLSaved(false), 3000);
    } catch { toast.error('Failed to save'); }
    finally { setLS(false); }
  };

  const saveAuto = async () => {
    setAS(true);
    try {
      await axios.put(`${API}/admin/whatsapp-automation/settings`, auto);
      toast.success('Automation settings saved');
      setASaved(true);
      setTimeout(() => setASaved(false), 3000);
    } catch { toast.error('Failed to save'); }
    finally { setAS(false); }
  };

  const setL = (k, v) => setLegacy(p => ({ ...p, [k]: v }));
  const setA = (k, v) => setAuto(p => ({ ...p, [k]: v }));

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-8">

        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
            <MessageSquare className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800">WhatsApp Automation</h1>
            <p className="text-sm text-slate-500">AiSensy-powered automated campaigns &amp; reminders</p>
          </div>
        </div>

        {/* ── Section 1: Automation Tracks ─────────────────────────────────── */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-slate-700">Automation Tracks</h2>
              <p className="text-xs text-slate-400 mt-0.5">All messages sent only between 10am – 7pm IST</p>
            </div>
            <span className="text-xs text-slate-400 flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" /> Day 0 / 1 / 2 / 4
            </span>
          </div>

          {autoLoading ? (
            <div className="flex justify-center py-10"><RefreshCw className="w-5 h-5 animate-spin text-slate-300" /></div>
          ) : (
            <div className="p-5 space-y-3">
              {['A', 'B', 'C'].map(t => (
                <TrackToggle
                  key={t}
                  track={t}
                  enabled={auto[`track_${t.toLowerCase()}_enabled`]}
                  onChange={v => setA(`track_${t.toLowerCase()}_enabled`, v)}
                />
              ))}

              {/* Offer text for Track C */}
              <div className="pt-2">
                <label className="text-sm font-medium text-slate-700 block mb-1.5">
                  Track C — Offer Text <span className="text-slate-400 font-normal">(shown in Day 2/4 messages)</span>
                </label>
                <input
                  type="text"
                  value={auto.offer_text}
                  onChange={e => setA('offer_text', e.target.value)}
                  placeholder="e.g. Get 20% off your first order with code FIRST20"
                  data-testid="offer-text-input"
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-300"
                />
              </div>

              {/* Alert email */}
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1.5">
                  Assistance Alert Email <span className="text-slate-400 font-normal">(notified when user replies "Need assistance")</span>
                </label>
                <input
                  type="email"
                  value={auto.alert_email}
                  onChange={e => setA('alert_email', e.target.value)}
                  placeholder="admin@yourdomain.com"
                  data-testid="alert-email-input"
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-300"
                />
              </div>

              {/* Frontend base URL */}
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1.5">
                  Portal Base URL <span className="text-slate-400 font-normal">(used in signup/KYC/dashboard links)</span>
                </label>
                <input
                  type="url"
                  value={auto.frontend_base_url}
                  onChange={e => setA('frontend_base_url', e.target.value)}
                  placeholder="https://connectdesk.cloudsetu.com"
                  data-testid="frontend-base-url-input"
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-green-300"
                />
              </div>

              <div className="pt-2 flex items-center gap-3">
                <Button
                  onClick={saveAuto}
                  disabled={autoSaving}
                  data-testid="save-automation-btn"
                  className="bg-green-600 hover:bg-green-700 text-white gap-2"
                >
                  {autoSaving ? <RefreshCw className="w-4 h-4 animate-spin" /> : autoSaved ? <CheckCircle className="w-4 h-4" /> : <Save className="w-4 h-4" />}
                  {autoSaving ? 'Saving…' : autoSaved ? 'Saved!' : 'Save Automation Settings'}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* ── Section 2: Webhook Info ───────────────────────────────────────── */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 bg-slate-50">
            <h2 className="text-sm font-semibold text-slate-700">AiSensy Webhook</h2>
            <p className="text-xs text-slate-400 mt-0.5">Configure this URL in AiSensy → Settings → Webhook</p>
          </div>
          <div className="px-5 py-5 space-y-3">
            <div className="bg-slate-50 rounded-xl border border-slate-200 px-4 py-3">
              <p className="text-xs text-slate-500 mb-1 font-medium">Webhook URL</p>
              <code className="text-sm font-mono text-slate-800 break-all">
                {process.env.REACT_APP_BACKEND_URL}/api/webhooks/aisensy
              </code>
            </div>
            <div className="text-xs text-slate-500 space-y-1">
              <p className="flex items-start gap-1.5"><span className="w-3 h-3 rounded-full bg-red-400 flex-shrink-0 mt-0.5" /> <span><strong>STOP</strong> reply → user is opted out of all tracks</span></p>
              <p className="flex items-start gap-1.5"><span className="w-3 h-3 rounded-full bg-amber-400 flex-shrink-0 mt-0.5" /> <span><strong>Need assistance</strong> reply → automation paused + alert email sent</span></p>
              <p className="flex items-start gap-1.5"><span className="w-3 h-3 rounded-full bg-green-400 flex-shrink-0 mt-0.5" /> <span>Delivery / read status → logged in message history</span></p>
            </div>
          </div>
        </div>

        {/* ── Section 3: Legacy KYC Reminder ───────────────────────────────── */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 bg-slate-50">
            <h2 className="text-sm font-semibold text-slate-700">Legacy KYC Reminder (Cron)</h2>
            <p className="text-xs text-slate-400 mt-0.5">One-time reminder for partners with KYC pending beyond the set delay</p>
          </div>

          {legacyLoading ? (
            <div className="flex justify-center py-10"><RefreshCw className="w-5 h-5 animate-spin text-slate-300" /></div>
          ) : (
            <div className="px-5 py-5 space-y-5">
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1.5">KYC Reminder Delay</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number" min={1} max={720}
                    value={legacy.kyc_reminder_delay_hours}
                    onChange={e => setL('kyc_reminder_delay_hours', Math.max(1, parseInt(e.target.value) || 1))}
                    data-testid="delay-input"
                    className="w-24 border border-slate-200 rounded-xl px-3 py-2 text-sm text-center focus:outline-none focus:ring-2 focus:ring-green-300"
                  />
                  <span className="text-sm text-slate-500">hours after registration</span>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1.5">
                  Daily Reminder Time <span className="text-slate-400 font-normal">(IST)</span>
                </label>
                <div className="flex items-center gap-2 flex-wrap">
                  <select value={legacy.kyc_reminder_time_hour} onChange={e => setL('kyc_reminder_time_hour', parseInt(e.target.value))}
                    data-testid="hour-select"
                    className="border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-300">
                    {HOURS.map(h => <option key={h} value={h}>{String(h).padStart(2, '0')}</option>)}
                  </select>
                  <span className="text-slate-400 font-semibold">:</span>
                  <select value={legacy.kyc_reminder_time_minute} onChange={e => setL('kyc_reminder_time_minute', parseInt(e.target.value))}
                    data-testid="minute-select"
                    className="border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-300">
                    {MINUTES.map(m => <option key={m} value={parseInt(m)}>{m}</option>)}
                  </select>
                  <div className="flex rounded-xl border border-slate-200 overflow-hidden">
                    {['AM', 'PM'].map(p => (
                      <button key={p} type="button" onClick={() => setL('kyc_reminder_ampm', p)}
                        data-testid={`ampm-${p.toLowerCase()}`}
                        className={`px-4 py-2 text-sm font-medium transition-colors ${legacy.kyc_reminder_ampm === p ? 'bg-green-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}>
                        {p}
                      </button>
                    ))}
                  </div>
                </div>
                <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                  <Info className="w-3 h-3" /> Cron runs every 30 min — reminder fires within 30 min of this time.
                </p>
              </div>

              <div className="pt-2 flex items-center gap-3">
                <Button onClick={saveLegacy} disabled={legacySaving} data-testid="save-settings-btn"
                  className="bg-green-600 hover:bg-green-700 text-white gap-2">
                  {legacySaving ? <RefreshCw className="w-4 h-4 animate-spin" /> : legacySaved ? <CheckCircle className="w-4 h-4" /> : <Save className="w-4 h-4" />}
                  {legacySaving ? 'Saving…' : legacySaved ? 'Saved!' : 'Save Settings'}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* ── Section 4: Message Logs ───────────────────────────────────────── */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <button
            type="button"
            onClick={() => setShowLogs(v => !v)}
            data-testid="toggle-logs-btn"
            className="w-full px-5 py-4 flex items-center justify-between border-b border-slate-100 bg-slate-50 hover:bg-slate-100 transition-colors"
          >
            <div>
              <h2 className="text-sm font-semibold text-slate-700 text-left">Message Logs</h2>
              <p className="text-xs text-slate-400 mt-0.5 text-left">Track A / B / C sent message history</p>
            </div>
            {showLogs ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          </button>

          {showLogs && (
            <div className="p-5">
              {/* Filters */}
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <div className="flex rounded-xl border border-slate-200 overflow-hidden">
                  {['', 'A', 'B', 'C', 'opt_out'].map(t => (
                    <button key={t} type="button"
                      onClick={() => { setLogTrack(t); fetchLogs(0, t); }}
                      data-testid={`log-filter-${t || 'all'}`}
                      className={`px-3 py-1.5 text-xs font-medium transition-colors ${logTrack === t ? 'bg-slate-700 text-white' : 'text-slate-600 hover:bg-slate-50'}`}>
                      {t ? `Track ${t}` : 'All'}
                    </button>
                  ))}
                </div>
                <button type="button" onClick={() => fetchLogs(logPage, logTrack)}
                  data-testid="refresh-logs-btn"
                  className="ml-auto flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 transition-colors">
                  <RefreshCw className={`w-3.5 h-3.5 ${logsLoading ? 'animate-spin' : ''}`} /> Refresh
                </button>
              </div>

              {logsLoading ? (
                <div className="flex justify-center py-8"><RefreshCw className="w-5 h-5 animate-spin text-slate-300" /></div>
              ) : logs.length === 0 ? (
                <div className="text-center py-10 text-slate-400">
                  <Send className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No messages sent yet</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto rounded-xl border border-slate-200">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 text-slate-500 uppercase tracking-wide">
                        <tr>
                          <th className="px-3 py-2.5 text-left">Phone</th>
                          <th className="px-3 py-2.5 text-left">Track</th>
                          <th className="px-3 py-2.5 text-left">Day</th>
                          <th className="px-3 py-2.5 text-left">Campaign</th>
                          <th className="px-3 py-2.5 text-left">Status</th>
                          <th className="px-3 py-2.5 text-left">Sent At</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {logs.map(log => (
                          <tr key={log.id} className="hover:bg-slate-50 transition-colors">
                            <td className="px-3 py-2.5 font-mono text-slate-700">+{log.phone}</td>
                            <td className="px-3 py-2.5">
                              <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                log.track === 'A' ? 'bg-amber-100 text-amber-700' :
                                log.track === 'B' ? 'bg-blue-100 text-blue-700' :
                                log.track === 'C' ? 'bg-violet-100 text-violet-700' :
                                'bg-slate-100 text-slate-600'
                              }`}>{log.track === 'opt_out' ? 'Opt-Out' : `Track ${log.track}`}</span>
                            </td>
                            <td className="px-3 py-2.5 text-slate-600">{log.day !== null && log.day !== undefined ? `Day ${log.day}` : '—'}</td>
                            <td className="px-3 py-2.5 font-mono text-slate-500 truncate max-w-[140px]">{log.campaign_name}</td>
                            <td className="px-3 py-2.5">
                              <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_BADGE[log.status] || 'bg-slate-100 text-slate-600'}`}>
                                {log.status}
                              </span>
                            </td>
                            <td className="px-3 py-2.5 text-slate-400 whitespace-nowrap">
                              {log.sent_at ? new Date(log.sent_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {logsTotal > LOG_LIMIT && (
                    <div className="flex items-center justify-between mt-3 text-xs text-slate-500">
                      <span>{logPage * LOG_LIMIT + 1}–{Math.min((logPage + 1) * LOG_LIMIT, logsTotal)} of {logsTotal}</span>
                      <div className="flex gap-2">
                        <button disabled={logPage === 0} onClick={() => fetchLogs(logPage - 1, logTrack)}
                          className="px-3 py-1 rounded-lg border disabled:opacity-40 hover:bg-slate-100">Prev</button>
                        <button disabled={(logPage + 1) * LOG_LIMIT >= logsTotal} onClick={() => fetchLogs(logPage + 1, logTrack)}
                          className="px-3 py-1 rounded-lg border disabled:opacity-40 hover:bg-slate-100">Next</button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Info box */}
        <div className="bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 text-xs text-amber-800 flex items-start gap-2">
          <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p><strong>10am–7pm IST only.</strong> Messages scheduled outside this window are held until the next morning.</p>
            <p><strong>No credentials in messages.</strong> Links point to the Portal Base URL above — never include passwords.</p>
            <p><strong>9 campaign templates</strong> must exist in AiSensy. Env vars: <code className="bg-amber-100 px-1 rounded">AISENSY_CAMPAIGN_TRACK_A_D0/D2/D4</code>, <code className="bg-amber-100 px-1 rounded">TRACK_B_D0/D1/D2/D4</code>, <code className="bg-amber-100 px-1 rounded">TRACK_C_D2/D4</code> — all are already set in your .env.</p>
          </div>
        </div>

      </div>
    </DashboardLayout>
  );
}
